import React from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Syncs the SWR infinite page count with the URL `?size=` parameter.
 *
 * Returns a setSize function to pass to the Table instead of swr.setSize.
 * It only updates `?size` in the URL, leaving other params (filters) intact.
 *
 * An effect watches `?size` and calls onSizeChange (swr.setSize) whenever it
 * changes — so data is fetched only when the URL changes, not on click.
 *
 * - Load more / load all: Table calls returned setSize → URL updates → effect
 *   fires → swr.setSize loads the pages.
 * - Filter submit: form replaces all params (removing ?size) → effect sees
 *   size drop to 1 → swr.setSize(1) resets to page 1.
 * - Browser back: full URL restored (including ?size=N) → effect fires →
 *   swr.setSize(N) reloads previously loaded pages.
 */
export const usePageSizeInUrl = (onSizeChange: (n: number) => void): (n: number | ((prev: number) => number)) => Promise<undefined> => {
  const [searchParams, setSearchParams] = useSearchParams();

  const size = Number(searchParams.get('size') || '1');

  React.useEffect(() => {
    onSizeChange(size);
  }, [size, onSizeChange]);

  return React.useCallback(
    (n: number | ((prev: number) => number)) => {
      setSearchParams(
        (prev) => {
          const resolved = typeof n === 'function' ? n(Number(prev.get('size') || '1')) : n;
          const next = new URLSearchParams(prev);
          if (resolved <= 1) next.delete('size');
          else next.set('size', String(resolved));
          return next;
        },
        { replace: true },
      );
      return Promise.resolve(undefined);
    },
    [setSearchParams],
  );
};
